import { AudioMutedOutlined, AudioOutlined, PhoneOutlined } from "@ant-design/icons";
import { Badge, Button, FloatButton, Popover, Space, Typography } from "antd";
import { useCallback, useEffect } from "react";
import styled from "styled-components";
import { CALL_STATES, SDK_STATUSES, useInternalDialer } from "../internalDialerContext";

const { Text } = Typography;

const StyledStatusDot = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${(props) => props.$color};
`;

const StyledDuration = styled(Text)`
  font-size: 24px;
  font-variant-numeric: tabular-nums;
  display: block;
  text-align: center;
  margin: 12px 0;
`;

const StyledActionRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 16px;
  padding: 8px 0;
`;

function formatDuration(seconds) {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
}

function getStatusColor(sdkStatus, callState) {
  if (callState === CALL_STATES.FAILED) return "#ff4d4f";
  switch (sdkStatus) {
    case SDK_STATUSES.CONNECTED:
      return "#52c41a";
    case SDK_STATUSES.CONNECTING:
      return "#faad14";
    default:
      return "#d9d9d9";
  }
}

function getCallStateLabel(callState) {
  switch (callState) {
    case CALL_STATES.CALLING:
      return "Calling...";
    case CALL_STATES.ACTIVE:
      return "Connected";
    case CALL_STATES.INCOMING:
      return "Incoming Call";
    case CALL_STATES.ENDED:
      return "Call Ended";
    case CALL_STATES.FAILED:
      return "Call Failed";
    default:
      return "Phone";
  }
}

function FloatingCallPopover() {
  const {
    isEnabled,
    sdkStatus,
    callState,
    callDuration,
    isMuted,
    error,
    isWidgetVisible,
    incomingCallId,
    connect,
    hangup,
    acceptIncoming,
    declineIncoming,
    toggleMute,
    showWidget,
    hideWidget,
  } = useInternalDialer();

  useEffect(() => {
    if (
      callState !== CALL_STATES.IDLE &&
      callState !== CALL_STATES.ENDED &&
      callState !== CALL_STATES.FAILED
    ) {
      showWidget();
    }
  }, [callState, showWidget]);

  const handleButtonClick = useCallback(async () => {
    if (isWidgetVisible) {
      hideWidget();
      return;
    }
    if (sdkStatus === SDK_STATUSES.DISCONNECTED) {
      await connect();
    }
    showWidget();
  }, [sdkStatus, isWidgetVisible, connect, showWidget, hideWidget]);

  if (!isEnabled) {
    return null;
  }

  const statusColor = getStatusColor(sdkStatus, callState);
  const isInCall = callState === CALL_STATES.CALLING || callState === CALL_STATES.ACTIVE;

  const popoverTitle = (
    <Space>
      <StyledStatusDot $color={statusColor} />
      <Text strong>{getCallStateLabel(callState)}</Text>
    </Space>
  );

  const renderCallStateContent = () => {
    if (callState === CALL_STATES.FAILED) {
      return <Text type="danger">{error || "Call could not be connected"}</Text>;
    }
    if (callState === CALL_STATES.INCOMING) {
      return (
        <StyledActionRow>
          <Button
            shape="circle"
            size="large"
            type="primary"
            icon={<PhoneOutlined />}
            onClick={() => acceptIncoming(incomingCallId)}
          />
          <Button
            shape="circle"
            size="large"
            danger
            type="primary"
            icon={<PhoneOutlined style={{ transform: "rotate(135deg)" }} />}
            onClick={() => declineIncoming(incomingCallId)}
          />
        </StyledActionRow>
      );
    }
    if (isInCall || callState === CALL_STATES.ENDED) {
      return (
        <>
          <StyledDuration>{formatDuration(callDuration)}</StyledDuration>
          <StyledActionRow>
            {callState === CALL_STATES.ACTIVE && (
              <Button
                shape="circle"
                size="large"
                type={isMuted ? "primary" : "default"}
                icon={isMuted ? <AudioMutedOutlined /> : <AudioOutlined />}
                onClick={toggleMute}
              />
            )}
            {isInCall && (
              <Button
                shape="circle"
                size="large"
                danger
                type="primary"
                icon={<PhoneOutlined style={{ transform: "rotate(135deg)" }} />}
                onClick={hangup}
              />
            )}
          </StyledActionRow>
        </>
      );
    }
    return (
      <Text type="secondary">
        {sdkStatus === SDK_STATUSES.CONNECTED
          ? "Ready to make calls"
          : "Connecting to voice service..."}
      </Text>
    );
  };

  const popoverContent = (
    <>
      {renderCallStateContent()}
      {error && callState !== CALL_STATES.FAILED && (
        <Text type="danger" style={{ display: "block", marginTop: 8 }}>
          {error}
        </Text>
      )}
    </>
  );

  return (
    <Popover
      title={popoverTitle}
      placement="leftTop"
      content={popoverContent}
      open={isWidgetVisible}
      trigger="click"
      onOpenChange={(open) => {
        if (!open) hideWidget();
      }}
    >
      <Badge dot offset={[-4, 4]} color={statusColor}>
        <FloatButton icon={<PhoneOutlined />} onClick={handleButtonClick} tooltip="Phone" />
      </Badge>
    </Popover>
  );
}

export default FloatingCallPopover;
